import {
  PunksmarketIndexerBid,
  PunksmarketIndexerCriteria,
} from '@/models/unified-market-bid';
import {
  collectColorRefs,
  formatColorIdList,
  shortColorListTag,
} from '@/utils/punksmarket-color-catalog';
import {
  formatTraitIdList,
  shortTraitListTag,
} from '@/utils/punksmarket-trait-catalog';

/** Trait ids used by PunksMarket / PunksData filter (indexer API). */
export const PUNKSMARKET_MAX_TRAIT_ID = 110;
/** Color ids used by PunksMarket filter (1..221 per indexer API). */
export const PUNKSMARKET_MAX_COLOR_ID = 221;

export type PunksmarketBidTargetKind = 'any' | 'allowlist' | 'criteria';

export interface PunksmarketCriteriaDisplay {
  kind: PunksmarketBidTargetKind;
  summary: string;
  /** Short labels for table tag pills. */
  tags: string[];
  /** Full sentences for bid details modal. */
  lines: string[];
  /** Palette swatches for modal (unique color ids referenced in criteria). */
  colorRefs: Array<{ id: number; hex: string }>;
}

function parseMask(mask: string | undefined | null): bigint {
  if (mask == null || mask === '' || mask === '0' || mask === '0x0') {
    return 0n;
  }
  try {
    return BigInt(mask);
  } catch {
    return 0n;
  }
}

/** Returns set bit indices up to maxId (inclusive). */
export function maskToIds(mask: string | undefined | null, maxId: number): number[] {
  const value = parseMask(mask);
  if (value === 0n) {
    return [];
  }
  const ids: number[] = [];
  for (let i = 0; i <= maxId; i++) {
    if ((value >> BigInt(i)) & 1n) {
      ids.push(i);
    }
  }
  return ids;
}

export function isPunksmarketCriteriaEmpty(
  criteria: PunksmarketIndexerCriteria | undefined,
): boolean {
  if (!criteria) {
    return true;
  }
  const hasMask =
    maskToIds(criteria.requiredTraitMask, PUNKSMARKET_MAX_TRAIT_ID).length > 0 ||
    maskToIds(criteria.forbiddenTraitMask, PUNKSMARKET_MAX_TRAIT_ID).length > 0 ||
    maskToIds(criteria.anyOfTraitMask, PUNKSMARKET_MAX_TRAIT_ID).length > 0 ||
    maskToIds(criteria.requiredColorMask, PUNKSMARKET_MAX_COLOR_ID).length > 0 ||
    maskToIds(criteria.forbiddenColorMask, PUNKSMARKET_MAX_COLOR_ID).length > 0 ||
    maskToIds(criteria.anyOfColorMask, PUNKSMARKET_MAX_COLOR_ID).length > 0;
  const hasRange =
    (criteria.minPixelCount ?? 0) > 0 ||
    (criteria.maxPixelCount ?? 0) > 0 ||
    (criteria.minColorCount ?? 0) > 0 ||
    (criteria.maxColorCount ?? 0) > 0;
  return !hasMask && !hasRange;
}

function formatIdList(label: string, ids: number[], maxListed = 8): string {
  if (ids.length === 0) {
    return '';
  }
  const listed = ids.slice(0, maxListed).map((id) => `#${id}`).join(', ');
  const suffix =
    ids.length > maxListed ? ` (+${ids.length - maxListed} more)` : '';
  return `${label}: ${listed}${suffix}`;
}

function formatCountRange(
  label: string,
  min: number | undefined,
  max: number | undefined,
): string | null {
  const lo = min ?? 0;
  const hi = max ?? 0;
  if (lo <= 0 && hi <= 0) {
    return null;
  }
  if (lo > 0 && hi > 0) {
    return `${label}: ${lo}–${hi}`;
  }
  if (lo > 0) {
    return `${label}: ≥ ${lo}`;
  }
  return `${label}: ≤ ${hi}`;
}

function shortIdListTag(prefix: string, ids: number[]): string | null {
  if (ids.length === 0) {
    return null;
  }
  if (ids.length === 1) {
    return `${prefix} #${ids[0]}`;
  }
  if (ids.length <= 3) {
    return `${prefix} ${ids.map((id) => `#${id}`).join(', ')}`;
  }
  return `${prefix} (${ids.length})`;
}

/**
 * Human-readable criteria for PunksMarket bids (trait/color masks, allowlists, ranges).
 */
export function formatPunksmarketCriteriaDisplay(
  criteria: PunksmarketIndexerCriteria | undefined,
  includeIds: number[] = [],
  excludeIds: number[] = [],
): PunksmarketCriteriaDisplay {
  const lines: string[] = [];
  const tags: string[] = [];

  const requiredTraits = maskToIds(criteria?.requiredTraitMask, PUNKSMARKET_MAX_TRAIT_ID);
  const forbiddenTraits = maskToIds(criteria?.forbiddenTraitMask, PUNKSMARKET_MAX_TRAIT_ID);
  const anyOfTraits = maskToIds(criteria?.anyOfTraitMask, PUNKSMARKET_MAX_TRAIT_ID);
  const requiredColors = maskToIds(criteria?.requiredColorMask, PUNKSMARKET_MAX_COLOR_ID);
  const forbiddenColors = maskToIds(criteria?.forbiddenColorMask, PUNKSMARKET_MAX_COLOR_ID);
  const anyOfColors = maskToIds(criteria?.anyOfColorMask, PUNKSMARKET_MAX_COLOR_ID);

  if (requiredTraits.length) {
    lines.push(formatTraitIdList('Required traits', requiredTraits));
    const t = shortTraitListTag('Requires', requiredTraits);
    if (t) tags.push(t);
  }
  if (forbiddenTraits.length) {
    lines.push(formatTraitIdList('Forbidden traits', forbiddenTraits));
    const t = shortTraitListTag('Excludes trait', forbiddenTraits);
    if (t) tags.push(t);
  }
  if (anyOfTraits.length) {
    lines.push(formatTraitIdList('Any of traits', anyOfTraits));
    const t = shortTraitListTag('Any of', anyOfTraits);
    if (t) tags.push(t);
  }
  if (requiredColors.length) {
    lines.push(formatColorIdList('Required colors', requiredColors));
    const t = shortColorListTag('Requires', requiredColors);
    if (t) tags.push(t);
  }
  if (forbiddenColors.length) {
    lines.push(formatColorIdList('Forbidden colors', forbiddenColors));
    const t = shortColorListTag('Excludes', forbiddenColors);
    if (t) tags.push(t);
  }
  if (anyOfColors.length) {
    lines.push(formatColorIdList('Any of colors', anyOfColors));
    const t = shortColorListTag('Any of', anyOfColors);
    if (t) tags.push(t);
  }

  const pixelRange = formatCountRange(
    'Pixel count',
    criteria?.minPixelCount,
    criteria?.maxPixelCount,
  );
  if (pixelRange) {
    lines.push(pixelRange);
    tags.push(pixelRange);
  }
  const colorRange = formatCountRange(
    'Color count',
    criteria?.minColorCount,
    criteria?.maxColorCount,
  );
  if (colorRange) {
    lines.push(colorRange);
    tags.push(colorRange);
  }

  const include = [...(includeIds ?? [])].sort((a, b) => a - b);
  const exclude = [...(excludeIds ?? [])].sort((a, b) => a - b);

  if (include.length) {
    lines.push(
      formatIdList(
        `Allowlist (${include.length} punk${include.length === 1 ? '' : 's'})`,
        include,
        12,
      ),
    );
    if (include.length <= 3) {
      include.forEach((id) => tags.push(`#${id}`));
    } else {
      tags.push(`${include.length} punks allowlist`);
    }
  }
  if (exclude.length) {
    lines.push(
      formatIdList(
        `Exclude list (${exclude.length} punk${exclude.length === 1 ? '' : 's'})`,
        exclude,
        12,
      ),
    );
    tags.push(`${exclude.length} excluded`);
  }

  const criteriaEmpty = isPunksmarketCriteriaEmpty(criteria);
  let kind: PunksmarketBidTargetKind = 'any';
  if (include.length > 0 && criteriaEmpty) {
    kind = 'allowlist';
  } else if (lines.length > 0 || exclude.length > 0) {
    kind = 'criteria';
  } else if (!criteriaEmpty) {
    kind = 'criteria';
  }

  let summary: string;
  if (kind === 'allowlist') {
    summary =
      include.length === 1
        ? `Targets punk #${include[0]}`
        : `${include.length} punks allowlist`;
  } else if (kind === 'criteria') {
    summary = tags.slice(0, 3).join(' · ') || 'Criteria filter';
  } else {
    summary = 'Any punk in collection';
  }

  const uniqueTags = [...new Set(tags)].slice(0, 6);
  const allColorIds = [
    ...requiredColors,
    ...forbiddenColors,
    ...anyOfColors,
  ];
  return {
    kind,
    summary,
    tags: uniqueTags.length ? uniqueTags : [summary],
    lines: lines.length ? lines : [summary],
    colorRefs: collectColorRefs(allColorIds),
  };
}

export function formatPunksmarketBidCriteria(
  bid: PunksmarketIndexerBid,
): PunksmarketCriteriaDisplay {
  return formatPunksmarketCriteriaDisplay(
    bid.criteria,
    bid.includeIds ?? [],
    bid.excludeIds ?? [],
  );
}

/** Unified stash-style bid type for PunksMarket indexer rows. */
export type PunksmarketUnifiedBidType = 'trait' | 'collection' | 'custom';

export function classifyPunksmarketIndexerBid(item: PunksmarketIndexerBid): {
  bidType: PunksmarketUnifiedBidType;
  bidTypeLabel: string;
  display: PunksmarketCriteriaDisplay;
} {
  const display = formatPunksmarketBidCriteria(item);

  if (display.kind === 'criteria') {
    return { bidType: 'trait', bidTypeLabel: 'Trait Bid', display };
  }
  if (display.kind === 'allowlist') {
    return { bidType: 'custom', bidTypeLabel: 'Custom Bid', display };
  }
  return { bidType: 'collection', bidTypeLabel: 'Collection Bid', display };
}
